/**
 * A simple, deterministic AI driver for a player slot. Used by the menu's
 * attract mode (demo run), headless balance probes and — later — as the
 * stand-in for a disconnected co-op player (docs/MULTIPLAYER.md).
 *
 * Pure function of sim state: reads the world, writes one InputFrame. Never
 * touches the sim RNG, so a bot-driven run is as deterministic as a human one
 * with the same inputs.
 *
 * Policy (cheap, no allocation):
 *   1. an enemy within `fleeRadius` → step directly away from it
 *   2. an enemy bullet closing within `bulletRadius` → sidestep perpendicular to it
 *   3. every `walkPeriod` s, walk `walkTime` s toward the nearest pickup (XP)
 *   4. otherwise stand still (the weapon only fires while stationary)
 */
import type { InputFrame } from './input';
import type { Sim } from '../sim/sim';
import { wrapDelta } from '../sim/world';
import { dhypot } from './dmath';

export interface BotOptions {
  fleeRadius: number;
  bulletRadius: number;
  walkPeriod: number;
  walkTime: number;
  /** Dash away when an enemy is closer than this (needs the charge). */
  dashRadius: number;
}

export const DEFAULT_BOT: BotOptions = { fleeRadius: 4.5, bulletRadius: 2.2, walkPeriod: 5, walkTime: 1.2, dashRadius: 1.6 };

export function botInput(sim: Sim, playerIndex: number, out: InputFrame, opt: BotOptions = DEFAULT_BOT): void {
  const w = sim.world;
  const p = w.players[playerIndex];
  out.mx = 0;
  out.my = 0;
  out.dash = false;
  out.action = false;
  if (!p.alive) return;
  const half = w.half;

  // Portals open: walk into the stable route (index 0).
  const portal = sim.run.portals[0];
  if (portal && sim.isClearPhase(sim.run.phase)) {
    const dx = wrapDelta(portal.x - p.x, half);
    const dy = wrapDelta(portal.y - p.y, half);
    const d = Math.sqrt(dx * dx + dy * dy); // sqrt is IEEE-exact; hypot is engine-dependent
    if (d > 0.4) {
      out.mx = dx / d;
      out.my = dy / d;
    }
    return;
  }

  // nearest ground enemy
  const e = w.enemies;
  let nd = 1e9;
  let ndx = 0;
  let ndy = 0;
  for (let i = 1; i < e.pool.high; i++) {
    if (!e.pool.alive[i] || Math.abs(e.z[i]) > 0.5) continue;
    const dx = wrapDelta(e.x[i] - p.x, half);
    const dy = wrapDelta(e.y[i] - p.y, half);
    const d2 = dx * dx + dy * dy;
    if (d2 < nd) {
      nd = d2;
      ndx = dx;
      ndy = dy;
    }
  }
  if (nd < opt.fleeRadius * opt.fleeRadius) {
    const d = Math.sqrt(nd) || 1;
    out.mx = -ndx / d;
    out.my = -ndy / d;
    out.dash = nd < opt.dashRadius * opt.dashRadius && p.dashChargesLeft > 0;
    return;
  }

  // incoming enemy bullet
  const pr = w.projectiles;
  const br2 = opt.bulletRadius * opt.bulletRadius;
  for (let i = 1; i < pr.pool.high; i++) {
    if (!pr.pool.alive[i] || pr.side[i] !== 1) continue;
    const dx = wrapDelta(pr.x[i] - p.x, half);
    const dy = wrapDelta(pr.y[i] - p.y, half);
    if (dx * dx + dy * dy > br2) continue;
    // closing?
    if (dx * pr.vx[i] + dy * pr.vy[i] < 0) {
      const l = dhypot(pr.vx[i], pr.vy[i]) || 1;
      // sidestep to whichever side is away from the bullet's current offset
      const side = dx * pr.vy[i] - dy * pr.vx[i] > 0 ? 1 : -1;
      out.mx = (-pr.vy[i] / l) * side;
      out.my = (pr.vx[i] / l) * side;
      return;
    }
  }

  // periodic gem walk
  const t = sim.run.elapsed;
  if (t % opt.walkPeriod < opt.walkTime) {
    const pk = w.pickups;
    let bd = 1e9;
    let bx = 0;
    let by = 0;
    for (let i = 1; i < pk.pool.high; i++) {
      if (!pk.pool.alive[i]) continue;
      const dx = wrapDelta(pk.x[i] - p.x, half);
      const dy = wrapDelta(pk.y[i] - p.y, half);
      const d2 = dx * dx + dy * dy;
      if (d2 < bd) {
        bd = d2;
        bx = dx;
        by = dy;
      }
    }
    if (bd < 1e9 && bd > 1) {
      const d = Math.sqrt(bd);
      out.mx = bx / d;
      out.my = by / d;
    }
  }
}

/** Upgrade pick policy for bots: offensive first, then whatever's offered. */
export function botPickUpgrade(sim: Sim, player = sim.pickingPlayer()): string {
  const o = sim.offersFor(player);
  const pref = ['damage', 'fireRate', 'projectile', 'crit', 'element', 'health'];
  for (const c of pref) {
    const u = o.find((x) => x.category === c);
    if (u) return u.id;
  }
  return o[0].id;
}
